import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm } from '../../components/ConfirmModal';

const fmt     = (cents) => `$${(cents / 100).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const AdminAffiliateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [affiliate, setAffiliate]     = useState(null);
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [payingOut, setPayingOut]     = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [msg, setMsg]                 = useState({ type: '', text: '' });

  const [editCode,        setEditCode]        = useState('');
  const [editCommission,  setEditCommission]  = useState('');
  const [editThreshold,   setEditThreshold]   = useState('');
  const [rejectReason,    setRejectReason]    = useState('');

  // Promo code form state
  const [promoCodeName,     setPromoCodeName]      = useState('');
  const [promoDiscount,     setPromoDiscount]      = useState('10');
  const [promoFirstTime,    setPromoFirstTime]     = useState(true);
  const [promoMaxRedeem,    setPromoMaxRedeem]     = useState('');
  const [promoExpiry,       setPromoExpiry]        = useState('');
  const [promoLoading,      setPromoLoading]       = useState(false);
  const [promoDeleting,     setPromoDeleting]      = useState(false);

  const load = useCallback(async () => {
    try {
      const [affRes, convRes] = await Promise.all([
        API.get(`/admin/affiliates/${id}`),
        API.get(`/admin/affiliates/${id}/conversions`),
      ]);
      const aff = affRes.data.affiliate;
      setAffiliate(aff);
      setConversions(convRes.data.conversions || []);
      setEditCode(aff.code || '');
      setEditCommission(aff.commissionPercent);
      setEditThreshold(aff.payoutThreshold);
      // Pre-fill code name with affiliate code only if no existing promo code
      if (!aff.stripePromotionCodeId) setPromoCodeName(aff.code);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 3000);
  };

  const syncStripeStatus = async () => {
    if (!affiliate.stripeConnectAccountId) {
      flash('warning', 'No Stripe Connect account ID on record for this affiliate.');
      return;
    }
    setSyncingStripe(true);
    try {
      await API.post(`/admin/affiliates/${id}/sync-connect`);
      flash('success', 'Stripe Connect status synced.');
      load();
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Sync failed.');
    } finally {
      setSyncingStripe(false);
    }
  };

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      await API.patch(`/admin/affiliates/${id}`, { status, rejectedReason: rejectReason });
      flash('success', `Affiliate ${status}.`);
      load();
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await API.patch(`/admin/affiliates/${id}`, {
        code:              editCode.trim().toUpperCase() || undefined,
        commissionPercent: Number(editCommission),
        payoutThreshold:   Number(editThreshold),
      });
      flash('success', 'Settings saved.');
      load();
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePromoCode = async () => {
    setPromoLoading(true);
    try {
      const codeName = promoCodeName.trim().toUpperCase() || affiliate.code;
      await API.post(`/admin/affiliates/${id}/promo-code`, {
        codeName,
        discountPercent:  Number(promoDiscount),
        firstTimeOnly:    promoFirstTime,
        maxRedemptions:   promoMaxRedeem ? Number(promoMaxRedeem) : null,
        expiresAt:        promoExpiry || null,
      });
      flash('success', `Promo code ${codeName} created in Stripe successfully.`);
      load();
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Failed to create promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleDeletePromoCode = async () => {
    const ok = await confirm({ title: 'Deactivate Promo Code', message: `Deactivate promo code ${affiliate.code} in Stripe? The affiliate's audience won't be able to use it anymore.`, confirmLabel: 'Deactivate', variant: 'danger' });
    if (!ok) return;
    setPromoDeleting(true);
    try {
      await API.delete(`/admin/affiliates/${id}/promo-code`);
      flash('success', 'Promo code deactivated.');
      load();
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Failed to deactivate promo code.');
    } finally {
      setPromoDeleting(false);
    }
  };

  const handleDeleteAffiliate = async () => {
    const ok = await confirm({
      title: 'Delete Affiliate',
      message: `Permanently delete ${affiliate.name} as an affiliate? This will deactivate their Stripe promo code, delete all ${conversions.length} conversion records, and remove their affiliate attribution. This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await API.delete(`/admin/affiliates/${id}`);
      navigate('/admin/affiliates');
    } catch (err) {
      // The server refuses to delete an affiliate who is still owed money,
      // because deleting drops every conversion record along with it. Surface
      // the amount and make forfeiting it a separate, deliberate confirmation.
      if (err.response?.data?.requiresForfeitConfirmation) {
        setDeleting(false);
        const forfeit = await confirm({
          title: 'Unpaid balance will be lost',
          message: `${affiliate.name} is still owed ${fmt(err.response.data.pendingAmount)}. Deleting them erases that balance and every conversion record. Send the payout first if they should be paid.`,
          confirmLabel: 'Delete and forfeit balance',
          variant: 'danger',
        });
        if (!forfeit) return;
        setDeleting(true);
        try {
          await API.delete(`/admin/affiliates/${id}`, { data: { confirmForfeit: true } });
          navigate('/admin/affiliates');
          return;
        } catch (err2) {
          flash('danger', err2.response?.data?.message || 'Failed to delete affiliate.');
          setDeleting(false);
          return;
        }
      }
      flash('danger', err.response?.data?.message || 'Failed to delete affiliate.');
      setDeleting(false);
    }
  };

  const triggerPayout = async () => {
    const ok = await confirm({ title: 'Send Payout', message: `Send ${fmt(affiliate.stats.commissionPending)} to ${affiliate.name} via Stripe Connect?`, confirmLabel: 'Send', variant: 'primary' });
    if (!ok) return;
    setPayingOut(true);
    try {
      const res = await API.post(`/admin/affiliates/${id}/payout`);
      flash('success', `Payout of ${fmt(res.data.transfer.amount)} sent successfully.`);
      load();
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Payout failed.');
    } finally {
      setPayingOut(false);
    }
  };

  if (loading) return (
    <AdminLayout title="Affiliate Detail">
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
      </div>
    </AdminLayout>
  );

  if (!affiliate) return (
    <AdminLayout title="Affiliate Detail">
      <p>Affiliate not found.</p>
    </AdminLayout>
  );

  const { stats } = affiliate;
  const canPayout = affiliate.stripeConnectOnboarded && stats.commissionPending >= affiliate.payoutThreshold * 100;

  return (
    <AdminLayout title={`Affiliate — ${affiliate.name}`}>
      <Link to="/admin/affiliates" style={{ color: '#6f42c1', fontSize: '13px' }}>← Back to Affiliates</Link>

      {msg.text && (
        <div className={`alert alert-${msg.type} rounded-3 mt-3`} style={{ fontSize: '14px' }}>{msg.text}</div>
      )}

      <div className="row g-4 mt-1">
        {/* Left column */}
        <div className="col-lg-4">

          {/* Profile card */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                  style={{ width: '48px', height: '48px', background: '#6f42c1', fontSize: '18px', flexShrink: 0 }}>
                  {affiliate.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--text-main)' }}>{affiliate.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{affiliate.email}</div>
                </div>
              </div>
              <div className="mb-2">
                <span style={{ fontSize: '11px', color: 'var(--text-soft)' }}>Code</span>
                {affiliate.code
                  ? <div style={{ fontWeight: 700, letterSpacing: '2px', color: '#6f42c1', fontSize: '18px' }}>{affiliate.code}</div>
                  : <div style={{ fontSize: '13px', color: '#dc2626', fontWeight: 600 }}>Not assigned — set in Commission settings</div>
                }
              </div>
              {affiliate.country && (
                <div className="mb-2">
                  <span style={{ fontSize: '11px', color: 'var(--text-soft)' }}>Country</span>
                  <div style={{ fontSize: '13px', color: 'var(--text-body)', fontWeight: 600 }}>{affiliate.country}</div>
                </div>
              )}
              {affiliate.websiteUrl && (
                <div className="mb-2">
                  <span style={{ fontSize: '11px', color: 'var(--text-soft)' }}>Website</span>
                  <div><a href={affiliate.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1', fontSize: '13px' }}>{affiliate.websiteUrl}</a></div>
                </div>
              )}
              {affiliate.bio && (
                <div className="mb-2">
                  <span style={{ fontSize: '11px', color: 'var(--text-soft)' }}>Bio</span>
                  <div style={{ fontSize: '13px', color: 'var(--text-body)' }}>{affiliate.bio}</div>
                </div>
              )}
              {affiliate.promoMethod && (
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-soft)' }}>Promo method</span>
                  <div style={{ fontSize: '13px', color: 'var(--text-body)' }}>{affiliate.promoMethod}</div>
                </div>
              )}
            </div>
          </div>

          {/* Status actions */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Status: <span style={{ textTransform: 'capitalize' }}>{affiliate.status}</span></h6>
              <div className="d-flex flex-column gap-2">
                {affiliate.status !== 'active' && (
                  <button className="btn rounded-pill fw-bold" style={{ background: '#dcfce7', color: '#059669', fontSize: '13px' }}
                    onClick={() => updateStatus('active')} disabled={saving}>
                    ✓ Approve
                  </button>
                )}
                {affiliate.status !== 'paused' && affiliate.status === 'active' && (
                  <button className="btn rounded-pill fw-bold" style={{ background: '#e0e7ff', color: '#4338ca', fontSize: '13px' }}
                    onClick={() => updateStatus('paused')} disabled={saving}>
                    ⏸ Pause
                  </button>
                )}
                {affiliate.status === 'paused' && (
                  <button className="btn rounded-pill fw-bold" style={{ background: '#dcfce7', color: '#059669', fontSize: '13px' }}
                    onClick={() => updateStatus('active')} disabled={saving}>
                    ▶ Reactivate
                  </button>
                )}
                {affiliate.status !== 'rejected' && (
                  <>
                    <input
                      className="form-control rounded-3"
                      placeholder="Rejection reason (optional)"
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      style={{ fontSize: '13px' }}
                    />
                    <button className="btn rounded-pill fw-bold" style={{ background: '#fee2e2', color: '#dc2626', fontSize: '13px' }}
                      onClick={() => updateStatus('rejected')} disabled={saving}>
                      ✕ Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Danger zone */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px', border: '1px solid #fee2e2' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-1" style={{ color: '#dc2626' }}>Danger Zone</h6>
              <p style={{ fontSize: '12px', color: 'var(--text-muted2)', marginBottom: '12px' }}>
                Permanently deletes this affiliate, all conversions, click records, and deactivates their Stripe promo code. Cannot be undone.
              </p>
              <button
                className="btn rounded-pill fw-bold w-100"
                style={{ background: '#fee2e2', color: '#dc2626', fontSize: '13px' }}
                onClick={handleDeleteAffiliate}
                disabled={deleting}
              >
                {deleting
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting…</>
                  : `🗑 Delete ${affiliate.name} permanently`}
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Commission settings</h6>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>
                  Affiliate code {!affiliate.code && <span style={{ color: '#dc2626', fontWeight: 600 }}>— not assigned yet</span>}
                </label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  value={editCode}
                  onChange={e => setEditCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                  placeholder="e.g. KATE20"
                  maxLength={20}
                  style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '1px' }}
                />
                <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '4px' }}>
                  This becomes their referral link: <strong>?aff={editCode || 'CODE'}</strong>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Commission % (you pay affiliate)</label>
                <input type="number" className="form-control rounded-3" value={editCommission}
                  onChange={e => setEditCommission(e.target.value)} min={0} max={100} style={{ fontSize: '14px' }} />
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Payout threshold ($)</label>
                <input type="number" className="form-control rounded-3" value={editThreshold}
                  onChange={e => setEditThreshold(e.target.value)} min={1} style={{ fontSize: '14px' }} />
              </div>
              <button className="btn rounded-pill fw-bold w-100" style={{ background: '#6f42c1', color: 'white', fontSize: '13px' }}
                onClick={saveSettings} disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-2"></span> : null}Save Settings
              </button>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="col-lg-8">

          {/* Stats */}
          <div className="row g-3 mb-4">
            {[
              { label: 'Clicks',       value: stats.clicks },
              { label: 'Signups',      value: stats.signups },
              { label: 'Conversions',  value: stats.conversions },
              { label: 'Revenue',      value: fmt(stats.revenueTotal) },
              { label: 'Earned',       value: fmt(stats.commissionEarned) },
              { label: 'Paid Out',     value: fmt(stats.commissionPaid) },
            ].map((s, i) => (
              <div key={i} className="col-6 col-md-4">
                <div className="card border-0 shadow-sm text-center p-3" style={{ borderRadius: '12px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)' }}>{s.value}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Payout */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                  <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Pending payout</h6>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#ea580c' }}>{fmt(stats.commissionPending)}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }} className="d-flex align-items-center gap-2 flex-wrap">
                    Stripe Connect: {affiliate.stripeConnectOnboarded
                      ? <span style={{ color: '#059669', fontWeight: 600 }}>✓ Connected</span>
                      : <span style={{ color: '#dc2626' }}>Not connected</span>}
                    {affiliate.stripeConnectAccountId && (
                      <button
                        className="btn btn-link p-0"
                        style={{ fontSize: '11px', color: '#6f42c1' }}
                        onClick={syncStripeStatus}
                        disabled={syncingStripe}
                        title="Re-check Stripe to update connection status"
                      >
                        {syncingStripe ? 'Syncing…' : '↻ Sync from Stripe'}
                      </button>
                    )}
                  </div>
                </div>
                <button
                  className="btn rounded-pill fw-bold px-4"
                  style={{ background: canPayout ? 'linear-gradient(135deg,#059669,#34d399)' : 'var(--header-bg)', color: canPayout ? 'white' : 'var(--text-soft)', fontSize: '14px' }}
                  onClick={triggerPayout}
                  disabled={!canPayout || payingOut}
                >
                  {payingOut
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
                    : `Send Payout via Stripe`}
                </button>
              </div>
              {!canPayout && affiliate.stripeConnectOnboarded && (
                <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '8px' }}>
                  Minimum payout is ${affiliate.payoutThreshold}. Current balance: {fmt(stats.commissionPending)}.
                </div>
              )}
              {!affiliate.stripeConnectOnboarded && (
                <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '8px' }}>
                  Affiliate must connect their bank account before payouts can be sent.
                </div>
              )}
            </div>
          </div>

          {/* Promo Code */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Stripe Promo Code</h6>

              {!affiliate.code ? (
                <p style={{ fontSize: '13px', color: 'var(--text-soft)' }}>
                  Assign an affiliate code first before creating a promo code.
                </p>
              ) : affiliate.stripePromotionCodeId ? (
                /* ── Existing code ── */
                <div>
                  <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                    <span style={{ fontWeight: 700, letterSpacing: '2px', fontSize: '18px', color: '#6f42c1' }}>
                      {affiliate.promoCodeName || affiliate.code}
                    </span>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                      background: affiliate.promoCodeActive ? '#dcfce7' : '#fee2e2',
                      color: affiliate.promoCodeActive ? '#059669' : '#dc2626',
                    }}>
                      {affiliate.promoCodeActive ? '● Active' : '● Inactive'}
                    </span>
                  </div>
                  <div className="row g-2 mb-3" style={{ fontSize: '13px', color: 'var(--text-body)' }}>
                    <div className="col-6">
                      <span style={{ color: 'var(--text-soft)', fontSize: '11px' }}>Discount</span>
                      <div className="fw-bold">{affiliate.discountPercent}% off</div>
                    </div>
                    <div className="col-6">
                      <span style={{ color: 'var(--text-soft)', fontSize: '11px' }}>First-time only</span>
                      <div className="fw-bold">{affiliate.promoCodeFirstTimeOnly ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="col-6">
                      <span style={{ color: 'var(--text-soft)', fontSize: '11px' }}>Max redemptions</span>
                      <div className="fw-bold">{affiliate.promoCodeMaxRedemptions ?? '∞ Unlimited'}</div>
                    </div>
                    <div className="col-6">
                      <span style={{ color: 'var(--text-soft)', fontSize: '11px' }}>Expires</span>
                      <div className="fw-bold">{fmtDate(affiliate.promoCodeExpiresAt)}</div>
                    </div>
                  </div>
                  <button
                    className="btn rounded-pill fw-bold"
                    style={{ background: '#fee2e2', color: '#dc2626', fontSize: '13px' }}
                    onClick={handleDeletePromoCode}
                    disabled={promoDeleting}
                  >
                    {promoDeleting
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Deactivating…</>
                      : '✕ Deactivate in Stripe'}
                  </button>
                </div>
              ) : (
                /* ── Create form ── */
                <div>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted2)', marginBottom: '16px' }}>
                    No promo code yet. Create one in Stripe so <strong>{affiliate.name}</strong> can share it with their audience.
                  </p>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>
                        Promo code name <span style={{ color: 'var(--text-soft)' }}>(what customers type at checkout)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        value={promoCodeName}
                        onChange={e => setPromoCodeName(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                        placeholder={affiliate.code}
                        maxLength={20}
                        style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '1px' }}
                      />
                      <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '4px' }}>
                        Letters, numbers, _ and - only. Leave blank to use <strong>{affiliate.code}</strong>.
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Discount % (audience gets)</label>
                      <input
                        type="number"
                        className="form-control rounded-3"
                        value={promoDiscount}
                        onChange={e => setPromoDiscount(e.target.value)}
                        min={1} max={100}
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                    <div className="col-sm-6 d-flex align-items-end">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="promoFirstTime"
                          checked={promoFirstTime}
                          onChange={e => setPromoFirstTime(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="promoFirstTime" style={{ fontSize: '13px', color: 'var(--text-body)' }}>
                          New customers only
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Max redemptions (blank = unlimited)</label>
                      <input
                        type="number"
                        className="form-control rounded-3"
                        value={promoMaxRedeem}
                        onChange={e => setPromoMaxRedeem(e.target.value)}
                        min={1}
                        placeholder="Unlimited"
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Expiry date (blank = never)</label>
                      <input
                        type="date"
                        className="form-control rounded-3"
                        value={promoExpiry}
                        onChange={e => setPromoExpiry(e.target.value)}
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                    <div className="col-12">
                      <button
                        className="btn rounded-pill fw-bold px-4"
                        style={{ background: 'linear-gradient(135deg,#6f42c1,#a78bfa)', color: 'white', fontSize: '13px' }}
                        onClick={handleCreatePromoCode}
                        disabled={promoLoading}
                      >
                        {promoLoading
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
                          : `Create "${(promoCodeName.trim() || affiliate.code)}" in Stripe`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Conversions */}

          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Conversions ({conversions.length})</h6>
              {conversions.length === 0 ? (
                <p style={{ color: 'var(--text-soft)', fontSize: '14px' }}>No conversions yet.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm" style={{ fontSize: '13px' }}>
                    <thead>
                      <tr style={{ color: 'var(--text-muted2)' }}>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Revenue</th>
                        <th>Commission</th>
                        <th>Status</th>
                        <th>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversions.map(c => (
                        <tr key={c._id}>
                          <td>{fmtDate(c.convertedAt)}</td>
                          <td style={{ textTransform: 'capitalize' }}>{c.type} {c.plan ? `(${c.plan})` : ''}</td>
                          <td>{fmt(c.revenueAmount)}</td>
                          <td style={{ color: '#059669', fontWeight: 600 }}>{fmt(c.commissionAmount)}</td>
                          <td>
                            <span style={{
                              fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                              background: c.status === 'paid' ? '#dcfce7' : '#fef9c3',
                              color: c.status === 'paid' ? '#059669' : '#713f12',
                            }}>
                              {c.status === 'paid' ? '✓ Paid' : 'Pending'}
                            </span>
                          </td>
                          <td style={{ color: 'var(--text-soft)' }}>{fmtDate(c.paidAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAffiliateDetail;
